import { FunctionComponent, useCallback, useEffect, useState } from "react";

import {
  DefaultButton,
  MessageBar,
  MessageBarType,
  Stack,
  TextField
} from "@bps/fluent-ui";

import {
  analyzeInput,
  looksLikeB2CTracePayload,
  TraceAnalysis
} from "./insightsParser.utils";
import { TraceReport } from "./TraceReport";

export const InsightsParser: FunctionComponent = () => {
  const [rawInput, setRawInput] = useState("");
  const [status, setStatus] = useState<"idle" | "parsed" | "error">("idle");
  const [error, setError] = useState("");
  const [reports, setReports] = useState<TraceAnalysis[]>([]);

  const tryPasteTraceFromClipboard = useCallback(() => {
    navigator.clipboard
      .readText()
      .then(text => {
        if (looksLikeB2CTracePayload(text)) {
          setRawInput(current => (current === text ? current : text));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    tryPasteTraceFromClipboard();

    const handleFocus = () => {
      tryPasteTraceFromClipboard();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        tryPasteTraceFromClipboard();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [tryPasteTraceFromClipboard]);

  const handleParse = () => {
    try {
      const result = analyzeInput(rawInput);
      setReports(result);
      setError("");
      setStatus("parsed");
    } catch (e) {
      setReports([]);
      setError(e instanceof Error ? e.message : "Failed to parse input.");
      setStatus("error");
    }
  };

  const handleClear = () => {
    setRawInput("");
    setReports([]);
    setError("");
    setStatus("idle");
  };

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <TextField
        label="AppInsights JSON"
        multiline
        resizable={false}
        rows={4}
        value={rawInput}
        placeholder='Paste JSON starting with [{ "Kind": "Headers", ... }]'
        onFocus={tryPasteTraceFromClipboard}
        onChange={(_, value) => {
          setRawInput(value ?? "");
          if (status !== "idle") {
            setStatus("idle");
            setError("");
            setReports([]);
          }
        }}
      />

      <Stack horizontal tokens={{ childrenGap: 8 }}>
        <DefaultButton text="Render" onClick={handleParse} />
        <DefaultButton text="Clear" onClick={handleClear} />
      </Stack>

      {status === "error" && (
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      )}

      {reports.map((report, index) => (
        <TraceReport
          // eslint-disable-next-line react/no-array-index-key
          key={`${report.correlationId}-${index}`}
          report={report}
          index={index}
        />
      ))}
    </Stack>
  );
};
